import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { map, Observable, Subject, tap } from 'rxjs';
import { CreateCardReminderRequest, ICardV2 } from '../interfaces/card';
import {
  CARD_REMINDER_STATUS,
  Call,
} from '../components/board-v3/board-v3.types';
import {
  BoardCard,
  BoardColumn,
  BoardFilterSet,
  BoardField,
  BoardFieldsResponse,
  BoardFunnel,
  BoardMassActionPayload,
  BoardMassActionResponse,
  BoardManager,
  CreateBoardColumnPayload,
  CreateCardPayload,
  CreateFunnelPayload,
  DynamicBoardCard,
  FieldType,
  GetAllCardsPayload,
  GetCardsPayload,
  GetColumnsPayload,
  GetFunnelsPayload,
  GetManagersParams,
  MergeCardsPayload,
  MoveCardPayload,
  ReorderBoardColumnsPayload,
  SaveFunnelFilterPayload,
  UpdateBoardColumnPayload,
  UpdateFunnelPayload,
} from '../components/board-v3/board-v3.types';
import { StackedSidebarService } from './stacked-sidebar.service';
import { SidebarType } from '../components/layout/stacked-sidebar/stacked-sidebar.interface';
import type { LeadConversionResult } from '../components/board-v3/sidebars/lead-conversion/lead-conversion.component';
import { ACTIVE_EMPLOYEES_PARAMS } from '../enum/constants';

@Injectable({
  providedIn: 'root',
})
export class BoardService {
  private readonly http = inject(HttpClient);
  private readonly stackedSidebarService = inject(StackedSidebarService);

  private readonly cardStatusChangedSubject = new Subject<{ cardId: string; status: string; funnelId?: string }>();
  public readonly cardStatusChanged$ = this.cardStatusChangedSubject.asObservable();

  private readonly cardRefreshRequestedSubject = new Subject<string>();
  readonly cardRefreshRequested$ = this.cardRefreshRequestedSubject.asObservable();

  requestCardRefresh(cardId: string): void {
    this.cardRefreshRequestedSubject.next(cardId);
  }

  public card: ICardV2 = null!;
  public isCardLoaded: boolean = false;

  // #region funnels

  public getFunnels(payload: GetFunnelsPayload): Observable<BoardFunnel[]> {
    return this.http
      .post<{ data: BoardFunnel[] }>(environment.baseURL + '/board/funnels', payload)
      .pipe(map((res) => res.data));
  }

  public createFunnel(payload: CreateFunnelPayload): Observable<BoardFunnel> {
    return this.http
      .post<{ data: BoardFunnel }>(environment.baseURL + '/board/funnel', payload)
      .pipe(map((res) => res.data));
  }

  public updateFunnel(funnelId: string, payload: UpdateFunnelPayload): Observable<BoardFunnel> {
    return this.http
      .put<{ data: BoardFunnel }>(environment.baseURL + `/board/funnel/${funnelId}`, payload)
      .pipe(map((res) => res.data));
  }

  public deleteFunnel(funnelId: string): Observable<void> {
    return this.http.delete(environment.baseURL + `/board/funnel/${funnelId}`).pipe(map(() => undefined));
  }

  /** Regenerates card positions in every stage keeping the current order, or newest first when `sortByCreatedAt` */
  public resetCardPositions(funnelId: string, sortByCreatedAt: boolean): Observable<void> {
    return this.http
      .post(environment.baseURL + `/board/funnel/${funnelId}/reset-positions`, { sort_by_created_at: sortByCreatedAt })
      .pipe(map(() => undefined));
  }

  // #endregion

  // #region columns

  public getColumns(payload: GetColumnsPayload): Observable<BoardColumn[]> {
    return this.http
      .post<{ data: BoardColumn[] }>(environment.baseURL + '/board/column/all', payload)
      .pipe(map((res) => res.data));
  }

  public getAllColumns(funnelId: string): Observable<BoardColumn[]> {
    return this.http
      .get<{ data: BoardColumn[] }>(environment.baseURL + '/board/column/all', { params: { funnel_id: funnelId } })
      .pipe(map((res) => res.data));
  }

  public getCalls(cardId: string): Observable<Call[]> {
    return this.http
      .get<{ data: Call[] }>(environment.baseURL + '/calls', { params: { card_id: cardId } })
      .pipe(map((res) => res.data));
  }

  public getCall(callId: string): Observable<Call> {
    return this.http.get<{ data: Call }>(environment.baseURL + `/calls/${callId}`).pipe(map((res) => res.data));
  }

  public deleteCall(callId: string): Observable<void> {
    return this.http.delete<void>(environment.baseURL + `/calls/${callId}`);
  }

  public createColumn(payload: CreateBoardColumnPayload): Observable<BoardColumn> {
    return this.http
      .post<{ data: BoardColumn }>(environment.baseURL + '/board/column', payload)
      .pipe(map((res) => res.data));
  }

  public updateColumn(columnId: string, payload: UpdateBoardColumnPayload): Observable<BoardColumn> {
    return this.http
      .put<{ data: BoardColumn }>(environment.baseURL + `/board/column/${columnId}`, payload)
      .pipe(map((res) => res.data));
  }

  public deleteColumn(columnId: string): Observable<void> {
    return this.http.delete(environment.baseURL + `/board/column/${columnId}`).pipe(map(() => undefined));
  }

  public reorderColumns(payload: ReorderBoardColumnsPayload): Observable<BoardColumn[]> {
    return this.http
      .post<{ data: BoardColumn[] }>(environment.baseURL + '/board/column/reorder', payload)
      .pipe(map((res) => res.data));
  }

  // #endregion

  // #region cards

  public getCard(cardId: string): Observable<ICardV2> {
    return this.http.get<{ data: ICardV2 }>(environment.baseURL + `/board/card/${cardId}`).pipe(
      map((res) => res.data),
      tap((card) => {
        this.card = card;
        this.isCardLoaded = true;
      }),
    );
  }

  /**
   * Loads a card without touching the shared `card` state, so several cards can be fetched in parallel.
   */
  public getCardDetails(cardId: string): Observable<ICardV2> {
    return this.http
      .get<{ data: ICardV2 }>(environment.baseURL + `/board/card/${cardId}`)
      .pipe(map((res) => res.data));
  }

  public updateCard(cardId: string, payload: Record<string, unknown>): Observable<ICardV2> {
    return this.http.put<{ data: ICardV2 }>(environment.baseURL + `/board/card/${cardId}`, payload).pipe(
      map((res) => res.data),
      tap((card) => {
        this.card = card;
        this.isCardLoaded = true;
      }),
    );
  }

  public resetCard(): void {
    this.card = null!;
    this.isCardLoaded = false;
  }

  public createCard(payload: CreateCardPayload): Observable<BoardCard> {
    return this.http
      .post<{ data: BoardCard }>(environment.baseURL + '/board/card', payload)
      .pipe(map((res) => res.data));
  }

  public moveCard(cardId: string, payload: MoveCardPayload): Observable<BoardCard> {
    return this.http
      .put<{ data: BoardCard }>(environment.baseURL + `/board/card/${cardId}`, payload)
      .pipe(map((res) => res.data));
  }

  public deleteCard(cardId: string): Observable<void> {
    return this.http.delete(environment.baseURL + `/board/card/${cardId}`).pipe(map(() => undefined));
  }

  public massAction(payload: BoardMassActionPayload): Observable<BoardMassActionResponse> {
    return this.http
      .post<{ data: BoardMassActionResponse }>(environment.baseURL + '/board/card/mass-action', payload)
      .pipe(map((res) => res.data));
  }

  /**
   * Merges the given cards into `target_id` and applies the chosen field values to it.
   */
  public mergeCards(payload: MergeCardsPayload): Observable<ICardV2> {
    return this.http
      .post<{ data: ICardV2 }>(environment.baseURL + '/board/card/merge', payload)
      .pipe(map((res) => res.data));
  }

  /** First group of cards in the funnel that share a phone or an email (empty when there are no duplicates) */
  public findDuplicateCards(funnelId: string): Observable<BoardCard[]> {
    return this.http
      .post<{ data: BoardCard[] | { items: BoardCard[] } }>(environment.baseURL + '/board/card/duplicates', {
        funnel_id: funnelId,
      })
      .pipe(
        map((res) => {
          const d = res.data as BoardCard[] | { items?: BoardCard[] };
          return Array.isArray(d) ? d : (d?.items ?? []);
        }),
      );
  }

  public getCards(payload: GetCardsPayload): Observable<BoardCard[]> {
    return this.http
      .post<{ data: BoardCard[] | { items: BoardCard[] } }>(environment.baseURL + '/board/card/list', payload)
      .pipe(
        map((res) => {
          const d = res.data as BoardCard[] | { items?: BoardCard[] };
          return Array.isArray(d) ? d : (d?.items ?? []);
        }),
      );
  }

  public getAllCards(payload: GetAllCardsPayload): Observable<BoardCard[]> {
    return this.http
      .post<{ data: BoardCard[] }>(environment.baseURL + '/board/card/list/all', payload)
      .pipe(map((res) => res.data));
  }

  public notifyCardStatusChanged(event: { cardId: string; status: string; funnelId?: string }): void {
    this.cardStatusChangedSubject.next(event);
  }

  // #endregion

  // #region timeline, comments, reminders

  public createComment(cardId: string, payload: any) {
    return this.http
      .post<any>(environment.baseURL + `/board/card/${cardId}/comment`, payload)
      .pipe(map((res) => res.data));
  }

  public updateComment(cardId: string, commentId: string, payload: { text: string }) {
    return this.http
      .put<any>(environment.baseURL + `/board/card/${cardId}/comment/${commentId}`, payload)
      .pipe(map((res) => res.data));
  }

  public deleteComment(cardId: string, commentId: string) {
    return this.http
      .delete<any>(environment.baseURL + `/board/card/${cardId}/comment/${commentId}`)
      .pipe(map((res) => res.data));
  }

  public createCardReminder(cardId: string, payload: CreateCardReminderRequest) {
    return this.http
      .post<any>(environment.baseURL + `/board/card/${cardId}/reminder`, payload)
      .pipe(map((res) => res.data));
  }

  public updateCardReminderStatus(cardId: string, reminderId: string, status: CARD_REMINDER_STATUS) {
    return this.http
      .put<any>(environment.baseURL + `/board/card/${cardId}/reminder/${reminderId}`, { status })
      .pipe(map((res) => res.data));
  }

  public updateCardReminder(cardId: string, reminderId: string, payload: CreateCardReminderRequest) {
    return this.http
      .put<any>(environment.baseURL + `/board/card/${cardId}/reminder/${reminderId}`, payload)
      .pipe(map((res) => res.data));
  }

  // #endregion

  // #region card sections

  public getCardSections(funnelId: string) {
    return this.http
      .get<any>(environment.baseURL + '/board/card-section', { params: { funnel_id: funnelId } })
      .pipe(map((res) => res.data));
  }

  public createCardSection(payload: any) {
    return this.http.post<any>(environment.baseURL + '/board/card-section', payload).pipe(map((res) => res.data));
  }

  public updateCardSection(sectionId: string, payload: any) {
    return this.http
      .put<any>(environment.baseURL + `/board/card-section/${sectionId}`, payload)
      .pipe(map((res) => res.data));
  }

  public deleteCardSection(sectionId: string) {
    return this.http.delete<any>(environment.baseURL + `/board/card-section/${sectionId}`).pipe(map((res) => res.data));
  }

  // #endregion

  // #region fields

  public getFields(funnelId: string): Observable<BoardField[]> {
    return this.http
      .get<any>(environment.baseURL + '/board/fields', {
        params: { funnel_id: funnelId },
      })
      .pipe(map((res) => res?.data));
  }

  public getBoardFields(funnelId: string, entityType?: string): Observable<BoardField[]> {
    const params: Record<string, string> = { funnel_id: funnelId };
    if (entityType) params['entity_type'] = entityType;

    return this.http.get<{ data: BoardField[] }>(environment.baseURL + '/board/fields', { params }).pipe(
      map((res) => {
        return res.data || [];
      }),
    );
  }

  public createField(payload: Record<string, unknown>): Observable<any> {
    return this.http.post<{ data: any }>(environment.baseURL + '/board/fields', payload).pipe(map((res) => res.data));
  }

  public updateField(fieldId: string, payload: Record<string, unknown>): Observable<any> {
    return this.http
      .put<{ data: any }>(environment.baseURL + `/board/fields/${fieldId}`, payload)
      .pipe(map((res) => res.data));
  }

  public reorderFields(
    funnelId: string,
    payload: { id: string; position: number }[],
    entityType?: string,
  ): Observable<BoardField[]> {
    const params: Record<string, string> = { funnel_id: funnelId };
    if (entityType) params['entity_type'] = entityType;

    return this.http
      .post<{ data?: BoardField[] } | BoardField[]>(environment.baseURL + '/board/fields/reorder', payload, { params })
      .pipe(map((res) => (Array.isArray(res) ? res : (res.data ?? []))));
  }

  public deleteField(fieldId: string): Observable<void> {
    return this.http.delete(environment.baseURL + `/board/fields/${fieldId}`).pipe(map(() => undefined));
  }

  public getFieldTypes(): Observable<FieldType[]> {
    return this.http
      .get<{ data: FieldType[] } | FieldType[]>(environment.baseURL + '/board/fields/types')
      .pipe(map((res) => (Array.isArray(res) ? res : ((res as { data: FieldType[] }).data ?? []))));
  }

  // #endregion

  // #region import / export

  public importCsv(
    funnelId: string,
    payload: {
      file_url: string;
      import_type: string;
      column_id: string;
      field_mappings: { csv_column: string; target_field: string }[];
      field_defaults?: { target_field: string; value: unknown }[];
    },
  ): Observable<any> {
    return this.http
      .post<{ data: any }>(environment.baseURL + `/board/${funnelId}/import`, payload)
      .pipe(map((res) => res.data));
  }

  public exportCards(
    funnelId: string,
    payload: {
      format: 'CSV' | 'JSON' | 'TOML';
      fields: { key: string; is_custom: boolean }[];
    },
  ): Observable<any> {
    return this.http
      .post<{ data: any }>(environment.baseURL + `/board/${funnelId}/export`, payload)
      .pipe(map((res) => res.data));
  }

  // #endregion

  // #region filters

  public getFilters(funnelId: string): Observable<BoardFilterSet[]> {
    return this.http
      .get<{ data: BoardFilterSet[] }>(environment.baseURL + '/board/filter', { params: { funnel_id: funnelId } })
      .pipe(map((res) => res.data));
  }

  public createFilter(payload: SaveFunnelFilterPayload): Observable<BoardFilterSet> {
    return this.http
      .post<{ data: BoardFilterSet }>(environment.baseURL + '/board/filter', payload)
      .pipe(map((res) => res.data));
  }

  public updateFilter(uuid: string, payload: SaveFunnelFilterPayload): Observable<BoardFilterSet> {
    return this.http
      .put<{ data: BoardFilterSet }>(environment.baseURL + `/board/filter/${uuid}`, payload)
      .pipe(map((res) => res.data));
  }

  public deleteFilter(uuid: string): Observable<void> {
    return this.http.delete(environment.baseURL + `/board/filter/${uuid}`).pipe(map(() => undefined));
  }

  // #endregion

  public getManagers(params: GetManagersParams = {}): Observable<BoardManager[]> {
    return this.http
      .get<{ data: BoardManager[] | { items: BoardManager[] } }>(environment.baseURL + '/employees', {
        params: { ...params, ...ACTIVE_EMPLOYEES_PARAMS } as { [k: string]: string | number },
      })
      .pipe(
        map((res) => {
          const data = res.data as BoardManager[] | { items?: BoardManager[] };
          if (Array.isArray(data)) return data;
          return Array.isArray(data?.items) ? data.items : [];
        }),
      );
  }

  public openLeadConversionSidebar(
    cardIds: string[],
  ): Observable<LeadConversionResult | undefined> {
    return this.stackedSidebarService.openSidebar(
      {
        type: SidebarType.BoardV3LeadConversion,
        width: '420px',
      },
      {
        cardIds,
      },
    );
  }

  public openMergeCardsSidebar(cardIds: string[]): Observable<MergeCardsPayload | undefined> {
    return this.stackedSidebarService.openSidebar(
      {
        type: SidebarType.BoardV3MergeCards,
        width: '100%',
      },
      {
        cardIds,
      },
    );
  }
}
